import {FieldValues} from "react-hook-form";
import {TransferDirectionEnum} from "../enums/TransferDirectionEnum";
import Transfer from "../types/Transfer";
import TransferForm from "../types/TransferForm";
import EventCity from "../../eventCities/types/EventCity";
import {SalesChannelEnum} from "../../common/enums/SalesChannelEnum";

/**
 * Maps form values to the structure expected by the API
 */
export function mapFormValuesToTransfer(
    values: FieldValues,
    direction: TransferDirectionEnum,
    languageId?: number,
    departureId?: string
): Partial<TransferForm> {
    const mappedData: Partial<Transfer> = {
        // Direction comes correctly assigned by parameter
        direction,
        // Personal data - Correct mapping according to Transfer interface
        passengerName: values.passengerName,
        passengerLastName: values.passengerLastName,
        passengerPhoneNumber: String(values.passengerPhoneNumber),
        passengerContactEmail: values.passengerContactEmail,
        passengerDepartment: values.department ? `/departments/${values.department}` : undefined,
        passengerDialCode: values.dialCode ? parseInt(values.dialCode?.split('-')[1]) : undefined,
        passengerIso2: values.dialCode ? values.dialCode?.split('-')[0] : undefined,
        
        // Documentation data
        nationality: values.nationality,
        documentType: values.documentType,
        documentNumber: values.invoicePassportCif,
        
        language: languageId,

        // Billing information
        invoiceFullName: values.invoiceFullName,
        invoiceAddress: values.invoiceAddress,
        
        // Comments and terms
        comments: values.comments,
        termsAccepted: values.termsAccepted,
        
        // References to other resources with IRIs
        event: values.event ? `/events/${values.event}` : undefined,
        cityEvent: values.eventCity ? `/city_events/${values.eventCity}` : undefined,
    };

    // Add transport data according to direction
    if (direction === TransferDirectionEnum.ARRIVAL) {
        mappedData.transport = parseInt(values.arrivalTransport);
        mappedData.transportDate = values.arrivalTransportDate;
        mappedData.transportTime = values.arrivalTransportTime;
        mappedData.numberOfPassengers = values.arrivalNumberOfPassengers ? parseInt(values.arrivalNumberOfPassengers) : undefined;
        mappedData.transportLocationFrom = values.arrivalLocationFrom ? `/transport_locations/${values.arrivalLocationFrom}` : undefined;
        mappedData.addressDetailsFrom = values.arrivalLocationFromDetails;
        mappedData.transportLocationTo = values.arrivalLocationTo ? `/transport_locations/${values.arrivalLocationTo}` : undefined;
        mappedData.addressDetailsTo = values.arrivalLocationToDetails;
        mappedData.transportReference = values.arrivalTransportReference;
        mappedData.transportReferenceNumber = values.arrivalTransportReferenceNumber;
        // Selected vehicle in ARRIVAL
        mappedData.selectedVehicle = values.arrivalVehicle ? parseInt(values.arrivalVehicle) : undefined;
        // Pass the departure IRI if it exists
        mappedData.departureTransfer = departureId || undefined;
    } else if (direction === TransferDirectionEnum.DEPARTURE) {
        mappedData.transport = values.departureTransport;
        mappedData.transportDate = values.departureTransportDate;
        mappedData.transportTime = values.departureTransportTime;
        mappedData.numberOfPassengers = values.departureNumberOfPassengers ? parseInt(values.departureNumberOfPassengers) : undefined;
        mappedData.transportLocationFrom = values.departureLocationFrom ? `/transport_locations/${values.departureLocationFrom}` : undefined;
        mappedData.addressDetailsFrom = values.departureLocationFromDetails;
        mappedData.transportLocationTo = values.departureLocationTo ? `/transport_locations/${values.departureLocationTo}` : undefined;
        mappedData.addressDetailsTo = values.departureLocationToDetails;
        mappedData.transportReference = values.departureTransportReference;
        mappedData.transportReferenceNumber = values.departureTransportReferenceNumber;
        // Selected vehicle in DEPARTURE
        mappedData.selectedVehicle = values.departureVehicle ? parseInt(values.departureVehicle) : undefined;
        mappedData.pickUpTime = values.pickUpTime;
    }

    return mappedData;
}


// ============================================================================
// HELPER FUNCTIONS - PackageWizard Mapping
// ============================================================================

/**
 * Extracts dial code and ISO2 from combined dial string
 * @param dial - Combined format "ISO2-DIALCODE" (e.g., "ES-34")
 */
function parseDialCode(dial?: string): { iso2?: string; dialCode?: number } {
    if (!dial) return {};
    const [iso2, dialCodeStr] = dial.split('-');
    return {
        iso2: iso2 || undefined,
        dialCode: dialCodeStr ? Number(dialCodeStr) : undefined,
    };
}

/**
 * Maps personal and billing data from BookSaveInput to Transfer format
 */
function mapPersonalAndBillingData(values: FieldValues, languageId?: number) {
    const { iso2, dialCode } = parseDialCode(values.dial);

    return {
        passengerName: values.name,
        passengerLastName: values.surname,
        passengerPhoneNumber: String(values.telephone),
        passengerContactEmail: values.email,
        passengerDialCode: dialCode,
        passengerIso2: iso2,
        nationality: values.nationality,
        documentType: values.documentType,
        documentNumber: values.invoicePassportCif,
        language: languageId,
        invoiceFullName: values.invoiceNameAndSurname,
        invoiceAddress: values.invoiceAddress,
    };
}

/**
 * Maps transport location data (location IDs to IRIs)
 */
function mapLocationData(prefix: string, values: FieldValues) {
    return {
        transportLocationFrom: values[`${prefix}LocationFrom`]
            ? `/transport_locations/${values[`${prefix}LocationFrom`]}`
            : undefined,
        addressDetailsFrom: values[`${prefix}LocationFromDetails`],
        transportLocationTo: values[`${prefix}LocationTo`]
            ? `/transport_locations/${values[`${prefix}LocationTo`]}`
            : undefined,
        addressDetailsTo: values[`${prefix}LocationToDetails`],
    };
}

/**
 * Maps transport data based on direction (Arrival or Departure)
 * Uses dynamic prefix pattern to avoid code duplication
 */
function mapTransportData(values: FieldValues, direction: TransferDirectionEnum) {
    const prefix = direction === TransferDirectionEnum.ARRIVAL ? 'arrival' : 'departure';

    const transportData: Partial<Transfer> = {
        transport: parseInt(values[`${prefix}Transport`]),
        transportDate: values[`${prefix}TransportDate`],
        transportTime: values[`${prefix}TransportTime`],
        numberOfPassengers: values[`${prefix}NumberOfPassengers`] ? parseInt(values[`${prefix}NumberOfPassengers`]) : undefined,
        ...mapLocationData(prefix, values),
        transportReference: values[`${prefix}TransportReference`],
        transportReferenceNumber: values[`${prefix}TransportReferenceNumber`],
        selectedVehicle: values[`${prefix}Vehicle`] ? parseInt(values[`${prefix}Vehicle`]) : undefined,
    };

    // Add departure-specific field only for DEPARTURE direction
    if (direction === TransferDirectionEnum.DEPARTURE) {
        transportData.pickUpTime = values.pickUpTime;
    }

    return transportData;
}

/**
 * Maps form values from PackageWizard to the structure expected by the API
 * Maps from BookSaveInput (combined booking + transfer form)
 *
 * @param values - Form data from BookSaveInput
 * @param direction - Direction
 * @param salesChannel - Sales channel (PACKAGE)
 * @param languageId - User's language ID
 * @param eventCity - Event city information
 * @returns Mapped transfer data ready for API submission
 */
export function mapFormValuesToTransferFromPackageWizard(
    values: FieldValues,
    direction: TransferDirectionEnum,
    salesChannel: SalesChannelEnum,
    languageId?: number,
    eventCity?: EventCity,
): Partial<TransferForm> {

    return {
        direction,
        ...mapPersonalAndBillingData(values, languageId),

        // Comments and terms
        comments: values.comments ?? undefined, // Not present in wizard form
        termsAccepted: values.privacyPolicy && values.contractTerms,

        // References to other resources with IRIs
        event: eventCity?.event ?? undefined,
        cityEvent: values.eventCity ? `/city_events/${values.eventCity}` : undefined,
        salesChannel: salesChannel,

        // Transport data (dynamically mapped based on direction)
        ...mapTransportData(values, direction),
    };
}

